import { useEffect, useState } from 'react';
import { supervisorApi } from '@/features/workspace/api';
import type { MeetingConsultation } from '@/features/student/types';
import '@/features/student/u129-meetings.css';

const ROLE_LABELS: Record<string, string> = {
  STUDENT: 'Обучающийся под руководством',
  SUPERVISOR: 'Научный руководитель',
};

type RosterStudent = { id: string; full_name: string; academia_id: string };

export const SupervisorMeetingsOverlay = ({ onClose }: { onClose: () => void }) => {
  const [meetings, setMeetings] = useState<MeetingConsultation[]>([]);
  const [students, setStudents] = useState<RosterStudent[]>([]);
  const [notice, setNotice] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState({
    to_role: 'STUDENT',
    student_id: '',
    date: '2026-08-18',
    time: '10:00',
    title: 'Академическая консультация',
    agenda: '',
  });

  useEffect(() => {
    supervisorApi.meetings().then(setMeetings).catch(() => setNotice('Не удалось открыть встречи.'));
    supervisorApi.module('supervision', { tab: 'students' }).then((payload) => {
      const rows = (payload.items as RosterStudent[] | undefined) ?? [];
      setStudents(rows);
      setDraft((current) => ({ ...current, student_id: current.student_id || rows[0]?.id || '' }));
    }).catch(() => setStudents([]));
  }, []);

  const createMeeting = async (event: React.FormEvent) => {
    event.preventDefault();
    const created = await supervisorApi.createMeeting(draft);
    setMeetings((rows) => [created, ...rows]);
    setComposerOpen(false);
    setNotice('Приглашение отправлено');
  };

  return (
    <div className="u129-root" role="dialog" aria-modal="true">
      <header className="u129-top">
        <button type="button" onClick={onClose}>← Назад</button>
        <div>
          <h1>Встречи и приглашения</h1>
          <p>SUPERVISOR · приглашения, Accept / Decline и повестка</p>
        </div>
        <button type="button" onClick={onClose}>Закрыть ×</button>
      </header>
      <main className="u129-content">
        {notice ? <div className="u129-note u129-toast">{notice}</div> : null}
        <section className="u129-hero">
          <div>
            <h3>Встречи и приглашения</h3>
            <p>Создавайте приглашения обучающимся, получайте Accept / Decline и сохраняйте повестку и историю договорённостей.</p>
          </div>
          <div className="u129-toolbar">
            <button type="button" className="u129-btn gold" onClick={() => setComposerOpen(true)}>+ Создать встречу</button>
          </div>
        </section>
        <section className="u129-panel">
          <header>
            <h3>Встречи и приглашения</h3>
            <span className="u129-chip">{meetings.length}</span>
          </header>
          <div className="u129-body">
            {meetings.length === 0 ? <div className="u129-note">Встреч пока нет.</div> : meetings.map((meeting) => (
              <div className="u129-row" key={meeting.id}>
                <div>
                  <strong>{meeting.title}</strong>
                  <small>{meeting.meeting_code || meeting.id} · {meeting.meeting_date || meeting.scheduled_at} · {meeting.meeting_time || ''}</small>
                </div>
                <div>
                  <strong>{ROLE_LABELS[meeting.from_role || 'SUPERVISOR']} → {meeting.student_name || ROLE_LABELS[meeting.to_role || 'STUDENT']}</strong>
                  <small>{meeting.agenda || meeting.notes || ''}</small>
                </div>
                <div><span className="u129-chip">{meeting.status}</span></div>
              </div>
            ))}
          </div>
        </section>
        <section className="u129-panel">
          <header><h3>Быстрые действия руководителя</h3></header>
          <div className="u129-body">
            <div className="u129-toolbar">
              <button type="button" className="u129-btn gold" onClick={() => setComposerOpen(true)}>+ Пригласить обучающегося</button>
            </div>
            <div className="u129-note">Повестка, получатель, дата, время и ответ участника сохраняются в одной записи встречи.</div>
          </div>
        </section>
      </main>
      {composerOpen ? (
        <>
          <div className="u129-backdrop" onClick={() => setComposerOpen(false)} />
          <section className="u129-modal" role="dialog" aria-label="Создать встречу">
            <header>
              <h3>Создать встречу</h3>
              <button type="button" className="u129-btn" onClick={() => setComposerOpen(false)}>×</button>
            </header>
            <form className="u129-body" onSubmit={(event) => void createMeeting(event)}>
              <div className="u129-grid two">
                <label className="u129-field">
                  <span>Получатель</span>
                  <select className="u129-select" value={draft.student_id} onChange={(event) => setDraft({ ...draft, student_id: event.target.value })}>
                    {students.map((student) => (
                      <option key={student.id} value={student.id}>{student.full_name} · {student.academia_id}</option>
                    ))}
                  </select>
                </label>
                <label className="u129-field">
                  <span>Дата</span>
                  <input className="u129-input" type="date" required value={draft.date} onChange={(event) => setDraft({ ...draft, date: event.target.value })} />
                </label>
                <label className="u129-field">
                  <span>Время</span>
                  <input className="u129-input" type="time" required value={draft.time} onChange={(event) => setDraft({ ...draft, time: event.target.value })} />
                </label>
                <label className="u129-field">
                  <span>Название</span>
                  <input className="u129-input" required value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />
                </label>
              </div>
              <label className="u129-field">
                <span>Agenda</span>
                <textarea className="u129-textarea" value={draft.agenda} onChange={(event) => setDraft({ ...draft, agenda: event.target.value })} />
              </label>
              <button className="u129-btn gold" type="submit">Отправить приглашение</button>
            </form>
          </section>
        </>
      ) : null}
    </div>
  );
};
